import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// CSRF: signed session-bound token from login/refresh/me, kept in memory (never a
// cookie), attached to every state-changing request via X-CSRF-Token.
let _csrf = null;
export function setCsrf(t) { if (t) _csrf = t; }
export function clearCsrf() { _csrf = null; }
api.interceptors.request.use((config) => {
  const m = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(m) && _csrf) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = _csrf;
  }
  return config;
});

// L3: transparently refresh an expired access token once, then retry (session rotation).
let _refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const url = original.url || "";
    const isAuthCall = /\/auth\/(login|refresh|register|logout|mfa)/.test(url);
    if (error.response?.status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        _refreshing = _refreshing || api.post("/auth/refresh");
        const rr = await _refreshing;
        setCsrf(rr?.data?.csrf_token);
        _refreshing = null;
        return api(original);
      } catch (e) {
        _refreshing = null;
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return (
      detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : typeof e === "string" ? e : e?.message || ""))
        .filter(Boolean)
        .join(" ") || "Your request could not be processed."
    );
  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message; // Stripe-style { message }
    if (detail.error && typeof detail.error.message === "string") return detail.error.message; // Stripe error object
    if (typeof detail.detail === "string") return detail.detail;
  }
  return "Something went wrong. Please try again.";
}

// Turn ANYTHING (axios error, API detail string/array/object, Stripe error, raw Error) into a
// readable string. Guarantees we never render "[object Object]" to a user.
export function extractErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  if (err == null) return fallback;
  if (typeof err === "string") return err;
  const data = err?.response?.data;
  if (data != null) {
    if (typeof data === "string") return data;
    if (data.detail != null) return formatApiErrorDetail(data.detail);
    if (typeof data.message === "string") return data.message;
    if (data.error && typeof data.error.message === "string") return data.error.message;
    return formatApiErrorDetail(data);
  }
  if (err.error && typeof err.error.message === "string") return err.error.message; // Stripe.js error
  if (typeof err.message === "string" && err.message && err.message !== "no-checkout-url") return err.message;
  if (err.detail != null) return formatApiErrorDetail(err.detail);
  return fallback;
}

export const STATUS_META = {
  awaiting_approval: { label: "Awaiting ARPS Approval", tone: "pending" },
  paid_pending_review: { label: "Paid — Pending ARPS Review", tone: "pending" },
  pending_assignment: { label: "Pending Assignment", tone: "pending" },
  awaiting_client_decision: { label: "Awaiting client decision", tone: "pending" },
  closed_unserved: { label: "Closed — Unserved", tone: "failed" },
  assigned: { label: "Assigned", tone: "progress" },
  in_progress: { label: "In Progress", tone: "progress" },
  served: { label: "Served", tone: "served" },
  not_served: { label: "Not Served", tone: "failed" },
  pending_review: { label: "Pending Review", tone: "pending" },
  completed: { label: "Completed", tone: "served" },
  rejected: { label: "Rejected", tone: "failed" },
};

export const DEADLINE_META = {
  on_track: { label: "On Track", tone: "ok" },
  approaching: { label: "Deadline Approaching", tone: "warn" },
  urgent: { label: "Urgent", tone: "urgent" },
  passed: { label: "Deadline Passed", tone: "passed" },
  no_deadline: { label: "No Deadline", tone: "muted" },
};

// severity for sorting/filtering (higher = more urgent)
export const DEADLINE_SEVERITY = { passed: 4, urgent: 3, approaching: 2, on_track: 1, no_deadline: 0 };

export const PAY_STATUS_META = {
  unpaid: { label: "Unpaid", tone: "pending" },
  partial: { label: "Partial", tone: "progress" },
  paid: { label: "Paid", tone: "served" },
  void: { label: "Void", tone: "failed" },
  issued: { label: "Issued", tone: "progress" },
  draft: { label: "Draft", tone: "pending" },
};

export function fmtMoney(currency, amount) {
  const n = Number(amount || 0);
  return `${currency || "USD"} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatDistance(m) {
  if (m == null) return "";
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
}

